import { useCallback, useRef, useState } from 'react';
import { Link } from 'wouter';
import { useAuth } from '../../auth/useAuth';
import { routes } from '../../lib/assessment/routes';
import { ACCOUNT_DESTINATION } from '../../lib/shell/navigation';
import { useDisclosure } from '../../lib/shell/useDisclosure';
import { Button } from '../ui/button';
import { AuthStatus } from '../AuthStatus';

/**
 * The account affordance in the Experience Shell header (specification 3.5–3.7).
 *
 * Three states, and the ranking between them is the point:
 *
 * - **Signed out.** Sign-in is *tertiary*. A visitor who has not taken an
 *   assessment has nothing to sign in to, so promoting authentication ahead of
 *   the assessment inverts the journey. The existing `AuthStatus` form is reused
 *   behind a disclosure rather than reimplemented or rendered inline: inline it
 *   would be a full form competing with the page's primary action, which section
 *   3.5 forbids, and reimplementing it would fork the auth surface.
 * - **Signed in.** One obvious route back into the visitor's own material, plus
 *   sign-out. The destination is the `ACCOUNT_DESTINATION` slot; **PO-12 is
 *   unresolved** and that constant is the only place it is named.
 * - **Password recovery.** A recovery session carries a valid token but is not
 *   proof the holder knew a password, so ordinary signed-in affordances are
 *   withheld and the only forward path is finishing the reset. Signing out is
 *   the escape, and without it this state has no exit — the sign-in form is not
 *   rendered here. Do not remove it.
 *
 * `loading` renders nothing. A placeholder in this slot flashes on every arrival
 * and says nothing useful.
 */
export type AccountAffordanceProps = {
  /**
   * Namespaces this instance's element id and test ids. The Experience Shell
   * renders one affordance in the header row and another inside the mobile
   * panel, and two elements sharing a DOM id is invalid regardless of which one
   * is visible at a given width.
   *
   * Required rather than defaulted. A default would let a third call site
   * collide silently with the header instance, which is the exact failure this
   * prop exists to prevent.
   */
  idPrefix: string;
  /**
   * How the signed-out state presents sign-in.
   *
   * `disclosure` is the desktop header: a tertiary control that reveals the
   * form, because a form rendered inline there would compete with the page's
   * primary action (specification section 3.5).
   *
   * `inline` is the mobile panel, which is itself already a disclosure.
   * Specification section 3.2 permits one level of interaction inside it and
   * prohibits a second, so the form is rendered directly.
   *
   * Required rather than defaulted, for the same reason as `idPrefix`: a
   * default would let a future call site silently inherit the wrong one.
   */
  layout: 'disclosure' | 'inline';
};

export function AccountAffordance({ idPrefix, layout }: AccountAffordanceProps) {
  const { status, signOut } = useAuth();

  // Disclosure state, used only by the `disclosure` layout. The hooks are
  // called unconditionally because React requires it, so in the `inline` layout
  // `open` never becomes true and neither ref is ever attached — `useDisclosure`
  // returns immediately every render. That is inert, not wiring waiting to be
  // reconnected: the mobile panel must never gain a second disclosure level
  // (specification section 3.2), which is the defect this layout split fixed.
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  useDisclosure(open, close, panelRef, triggerRef);

  if (status === 'loading') return null;

  if (status === 'password-recovery') {
    return (
      <div className="flex items-center gap-3" data-testid={`${idPrefix}-recovery`}>
        <Link
          href={routes.updatePassword}
          className="text-sm text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          Finish setting your new password
        </Link>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid={`${idPrefix}-sign-out`}
          onClick={() => {
            void signOut();
          }}
        >
          Sign out
        </Button>
      </div>
    );
  }

  if (status === 'signed-in') {
    return (
      /*
        Order and wording follow the legacy public header exactly: "Log Out"
        first, then "Dashboard". Legacy calls this control "Log Out" here and
        "Sign Out" on the dashboard page itself; that inconsistency is legacy's,
        and each surface keeps the word legacy uses on it.
      */
      <div className="flex items-center gap-3" data-testid={`${idPrefix}-signed-in`}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid={`${idPrefix}-sign-out`}
          onClick={() => {
            void signOut();
          }}
        >
          Log Out
        </Button>
        <Link
          href={ACCOUNT_DESTINATION.href}
          data-testid={`${idPrefix}-destination`}
          className="text-sm text-foreground underline-offset-4 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {ACCOUNT_DESTINATION.label}
        </Link>
      </div>
    );
  }

  // Inside the mobile panel the surrounding panel *is* the disclosure. Adding
  // another here would make sign-in two interactions deep on the smallest
  // screen, which section 3.2 prohibits. Sign-in stays available; only the
  // wrapper differs.
  if (layout === 'inline') {
    return (
      <div data-testid={`${idPrefix}-signed-out`}>
        {/* An entry point, not a gate: nothing on the page behind this panel is
            what the visitor came to sign in *for*, so the dashboard is the
            destination. See the note on `AuthStatus.redirectTo`. */}
        <AuthStatus redirectTo={routes.dashboard} />
      </div>
    );
  }

  return (
    <div className="relative" data-testid={`${idPrefix}-signed-out`}>
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size="sm"
        aria-expanded={open}
        aria-controls={`${idPrefix}-panel`}
        data-testid={`${idPrefix}-trigger`}
        onClick={() => setOpen((previous) => !previous)}
      >
        {/* "Sign In", not "Sign in": the legacy public header capitalises both
            words, and V2-018C-2 brings this label to legacy parity. */}
        Sign In
      </Button>
      {open ? (
        <div
          ref={panelRef}
          id={`${idPrefix}-panel`}
          data-testid={`${idPrefix}-panel`}
          className="absolute right-0 z-40 mt-2 w-72 rounded-xl border border-border bg-popover p-4 text-popover-foreground shadow"
        >
          {/* Entry point — same reasoning as the inline layout above. */}
          <AuthStatus redirectTo={routes.dashboard} />
        </div>
      ) : null}
    </div>
  );
}
